import React, { useRef, useState } from 'react';
import { Camera as CameraIcon, Video, Image as ImageIcon, Upload, X } from 'lucide-react';
import { capturePhoto, pickFromLibrary, fileToPayload, isNativePlatform } from '../lib/mediaCapture';

/**
 * MediaCaptureMenu — unified "Add photo/video" button that surfaces:
 *   - 📷 Take photo  (live camera, native = Capacitor Camera, web = <input capture>)
 *   - 🎥 Record video (web = <input capture accept="video/*">, native = library + capture flag)
 *   - 📁 From library (existing file picker behavior)
 *
 * Calls `onCapture(payload)` with a normalized payload — either a File (web)
 * or a { dataUrl, type, size, blob } object (native). The caller's existing
 * handler can branch on which it receives.
 *
 * Props:
 *   onCapture(payload, kind) — kind = 'photo' | 'video' | 'library'
 *   accept                  — 'image' | 'video' | 'both' (default 'both')
 *   multiple                — allow multi-file from library? (default false)
 *   label                   — button text (default "Add photo or video")
 *   className               — applied to the outer trigger button
 *   testId                  — data-testid for the trigger
 */
const MediaCaptureMenu = ({
  onCapture,
  accept = 'both',
  multiple = false,
  label = 'Add photo or video',
  className = '',
  testId = 'media-capture-trigger',
  // Opt-in: surfaces a "respect the room" line when filming training
  // partners / instructors. Combatives dialogs pass this; general capture
  // (promotion photos, future body-progress photos) leaves it off.
  recordingEtiquette = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  const native = isNativePlatform();
  const canPhoto = accept === 'photo' || accept === 'image' || accept === 'both';
  const canVideo = accept === 'video' || accept === 'both';

  const handleTakePhoto = async () => {
    setMenuOpen(false);
    if (native) {
      try {
        setBusy(true);
        const result = await capturePhoto();
        if (result) onCapture(result, 'photo');
      } catch (err) {
        console.warn('Camera capture failed:', err?.message);
      } finally {
        setBusy(false);
      }
    } else {
      // Web: use hidden <input capture> — pops native camera on mobile
      photoInputRef.current?.click();
    }
  };

  const handleRecordVideo = () => {
    setMenuOpen(false);
    // Web: hidden <input accept="video/*" capture>. Native has no video API
    // in @capacitor/camera (it's photo-only), so we fall through to the same
    // path — Capacitor wraps the WebView so the input still triggers the
    // native video capture flow on iOS/Android.
    videoInputRef.current?.click();
  };

  const handleFromLibrary = async () => {
    setMenuOpen(false);
    if (native) {
      try {
        setBusy(true);
        const result = await pickFromLibrary();
        if (result) onCapture(result, 'library');
      } catch (err) {
        console.warn('Library pick failed:', err?.message);
      } finally {
        setBusy(false);
      }
    } else {
      libraryInputRef.current?.click();
    }
  };

  const handleInputFile = (kind) => async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Convert browser File → unified attachment payload. Persists the blob
    // to device storage (native = Filesystem, web = in-memory Blob URL)
    // and produces { kind, name, type, size, capturedAt, localUri }.
    if (multiple) {
      for (const file of files) {
        const payload = await fileToPayload(file);
        onCapture(payload, kind);
      }
    } else {
      const payload = await fileToPayload(files[0]);
      onCapture(payload, kind);
    }
    e.target.value = '';
  };

  // Build accept string for the library file input
  const libraryAccept = accept === 'photo' || accept === 'image'
    ? 'image/*'
    : accept === 'video'
      ? 'video/*'
      : 'image/*,video/*';

  return (
    <>
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        disabled={busy}
        data-testid={testId}
        className={className || 'w-full px-4 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-dashed border-enf-surface-border hover:border-enf-accent-fg rounded-md text-sm font-medium text-enf-text-secondary hover:text-enf-text-primary transition-colors flex items-center justify-center gap-2 disabled:opacity-60'}
      >
        <CameraIcon className="w-4 h-4" />
        {busy ? 'Opening…' : label}
      </button>

      {/* Hidden inputs — used for web fallback + library picker.
          `capture="environment"` requests the rear-facing camera on mobile.  */}
      {canPhoto && (
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple={multiple}
          onChange={handleInputFile('photo')}
          className="hidden"
        />
      )}
      {canVideo && (
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          capture="environment"
          multiple={multiple}
          onChange={handleInputFile('video')}
          className="hidden"
        />
      )}
      <input
        ref={libraryInputRef}
        type="file"
        accept={libraryAccept}
        multiple={multiple}
        onChange={handleInputFile('library')}
        className="hidden"
      />

      {/* Action sheet — slides up from bottom on mobile, anchors center on desktop */}
      {menuOpen && (
        <>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
            data-testid="media-capture-backdrop"
          />
          <div className="fixed inset-x-4 bottom-4 md:inset-auto md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-sm md:w-full z-[81]">
            <div className="bg-enf-surface-deep border border-enf-surface-border rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-enf-surface-border">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-enf-accent-fg font-semibold mb-0.5">Capture</div>
                  <div className="font-display text-xl tracking-wide leading-none">Where from?</div>
                  <div className="text-[10px] text-enf-text-muted mt-2 max-w-xs leading-snug">
                    Photos and videos stay on your device. Enferno saves only the filename — the file is yours, and yours to delete.
                  </div>
                  {recordingEtiquette && (
                    <div className="text-[10px] text-enf-text-muted mt-1.5 max-w-xs leading-snug">
                      Filming partners or your coach? Get their OK before you post — what&apos;s drilled in the room is theirs to share, not yours.
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close"
                  className="p-1.5 text-enf-text-muted hover:text-enf-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-2">
                {canPhoto && (
                  <button
                    onClick={handleTakePhoto}
                    data-testid="media-capture-photo"
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-enf-surface-mid rounded-md transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-enf-accent/15 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
                      <CameraIcon className="w-4 h-4 text-enf-accent-fg" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Take photo</div>
                      <div className="text-xs text-enf-text-muted">Open the camera now</div>
                    </div>
                  </button>
                )}
                {canVideo && (
                  <button
                    onClick={handleRecordVideo}
                    data-testid="media-capture-video"
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-enf-surface-mid rounded-md transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-enf-accent/15 border border-enf-accent-fg/30 flex items-center justify-center flex-shrink-0">
                      <Video className="w-4 h-4 text-enf-accent-fg" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Record video</div>
                      <div className="text-xs text-enf-text-muted">Capture training in motion</div>
                    </div>
                  </button>
                )}
                <button
                  onClick={handleFromLibrary}
                  data-testid="media-capture-library"
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-enf-surface-mid rounded-md transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-enf-surface-mid border border-enf-surface-border flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-4 h-4 text-enf-text-secondary" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">From library</div>
                    <div className="text-xs text-enf-text-muted">Pick from existing photos / videos</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MediaCaptureMenu;
